import { oldIconRegex } from 'constants/icon';
import { Icons8IllustrationSearchResponse, Icons8SearchResponse } from 'libs/e1api/icons8';
import { E1_ICON_ID_TO_LINE_ICON_TYPE_MAP } from 'widgets/Lines/LineIconWidget/LineIconWidget.config';

import { EXCLUDE_ICONS8_ILLUSTRATION_ICON_IDS } from '../usePaginatedIconSearch/usePaginatedIconSearch.config';
import { SearchApiResponse } from '../usePaginatedIconSearch/usePaginatedIconSearch.types';
import { IconHit } from './usePaginatedIcon.types';

const filterOutOldIcons = (icon: IconHit) => {
  return !!E1_ICON_ID_TO_LINE_ICON_TYPE_MAP[icon.id] || !oldIconRegex.test(icon.id);
};

const flattenHits = (d: SearchApiResponse) => d.hits;
export const parseAlgoliaIcons = (data: SearchApiResponse[]) => {
  if (!data) return [];

  return data.filter(Boolean).flatMap(flattenHits).filter(filterOutOldIcons);
};

/**
 * Flatten Icons8 search responses and filters out explicit, non-svg icons
 *
 * @param data
 * @returns
 */
export const parseIcons8Icons = (data: Icons8SearchResponse[]) => {
  if (!data) return [];

  // Same as E1 - filter out explicit icons
  const BLOCKED_CATEGORIES = ['Explicit', 'Sex Toy'];

  const icons: { [key: string]: boolean } = {};

  return data
    .filter(Boolean)
    .flatMap((d) => d.icons)
    .filter((icon) => {
      if (!icon) return false;

      const includeIcon = icon.sourceFormat === 'svg' && !BLOCKED_CATEGORIES.includes(icon.category);

      if (includeIcon && !icons[icon.id]) {
        icons[icon.id] = true;
        return true;
      }

      return false;
    });
};

/**
 * Parse illustration icons
 * Should only include icons available in svg format and have preview thumb
 * @param data
 * @returns
 */
export const parseIcons8Illustrations = (data: Icons8IllustrationSearchResponse[]) => {
  if (!data) return [];

  const icons: { [key: string]: boolean } = {};

  const result = data
    .filter(Boolean)
    .flatMap((d) => d.illustrations)
    .filter((icon) => {
      const inInExcludeList = EXCLUDE_ICONS8_ILLUSTRATION_ICON_IDS.includes(icon?.id);
      const includeIcon = icon && !inInExcludeList && icon.mediaResources?.includes('svg') && !!icon.urls?.thumb?.url;

      if (includeIcon && !icons[icon.id]) {
        icons[icon.id] = true;
        return true;
      }

      return false;
    });

  return result;
};

/**
 * Check if there are more icons8 icons to load based on the last search response received
 *
 * @param lastIcons8Response The last Icons8 search response
 * @param currentPage The current page number
 * @returns boolean
 */
export const hasMoreIcons8IllustrationIcons = (
  lastIcons8Response: Icons8IllustrationSearchResponse,
  currentPage: number,
  hitsPerPage: number,
) => {
  if (!lastIcons8Response || !lastIcons8Response.total) return false;

  const totalNumPages = Math.ceil(lastIcons8Response.total / hitsPerPage);

  return currentPage < totalNumPages - 1;
};
